/**
 *  @author:        Cloud for Good
 *  @description:   Applies custom Advancement logic to the SFRA js/cart/cart.js client-side logic. Full overwrite of the SFRA file because none
 *                  of the methods are public/extendible.
 *
 *  History
 *  <Date>        <Author Name>       <Reference #>       <Change Description>
 *  2022-09-30    Cloud for Good       N/A                Initial creation
 *  2024-06-20    Radhika Sharma       CFG-51             Update so that cart is updated when years to give drop down is changed
 *  2024-06-25    Radhika Sharma       CFG-50             Update so that cart remove product modal is displayed after a product is saved
 *  2024-08-15    Jassin Yacks         CFG-475            Enhanced the frequency drop-down change event listener by adding logic to dynamically display or hide the ".guestCheckoutPanel" div, depending on whether the cart contains a recurring item.
 *  2024-09-17    Jassin Yacks         CFG-438            Updated the "Save for later" button process flow.
 *  2024-10-08    Radhika Sharma       CFG-617            Update so that frequency field is more selectively retrieved and passed to front end components
 *  2024-10-22    Radhika Sharma       CFG-688            Update so that guest checkout panel is hidden/shown when a product's frequency is changed or a product is removed
 *  2024-10-24    Radhika Sharma       CFG-687            Update so that sign in message is hidden/shown when a product's frequency is changed or a product is removed
 *  2025-03-13    Jassin Yacks         CFG-776            Updated code to refresh line item details on the summary and product cards within the cart.
 *  2025-03-06    Jassin Yacks         CFG-775            Cart: Gift Summary - Physical Goods
 *  2025-03-20    Jassin Yacks         CFG-855            Integrates the promotion code form into the cart page.
*/

'use strict';

var base = require('base/product/base');
var focusHelper = require('base/components/focus');

/**
 * appends params to a url
 * @param {string} url - Original url
 * @param {Object} params - Parameters to append
 * @returns {string} result url with appended parameters
 */
function appendToUrl(url, params) {
    var newUrl = url;
    newUrl += (newUrl.indexOf('?') !== -1 ? '&' : '?') + Object.keys(params).map(function (key) {
        return key + '=' + encodeURIComponent(params[key]);
    }).join('&');

    return newUrl;
}

/**
 * Checks whether the basket is valid. if invalid displays error message and disables
 * checkout button
 * @param {Object} data - AJAX response from the server
 */
function validateBasket(data) {
    if (data.valid.error) {
        if (data.valid.message) {
            var errorHtml = '<div class="alert alert-danger alert-dismissible valid-cart-error ' +
                'fade show" role="alert">' +
                '<button type="button" class="close" data-dismiss="alert" aria-label="Close">' +
                '<span aria-hidden="true">&times;</span>' +
                '</button>' + data.valid.message + '</div>';

            $('.cart-error').append(errorHtml);
        } else {
            $('.cart').empty().append('<div class="row"> ' +
                '<div class="col-12 text-center"> ' +
                '<h1>' + data.resources.emptyCartMsg + '</h1> ' +
                '</div> ' +
                '</div>'
            );
            $('.number-of-items').empty().append(data.resources.numberOfItems);
            $('.minicart-quantity').empty().append(data.numItems);
            $('.minicart-link').attr({
                'aria-label': data.resources.minicartCountOfItems,
                title: data.resources.minicartCountOfItems
            });
            $('.minicart .popover').empty();
            $('.minicart .popover').removeClass('show');
        }

        $('.checkout-btn').addClass('disabled');
    } else {
        $('.checkout-btn').removeClass('disabled');
    }
}

/**
 * re-renders the order totals and the number of items in the cart
 * @param {Object} data - AJAX response from the server
 */
function updateCartTotals(data) {
    $('.grand-total').empty().append(data.totals.grandTotal);
    $('.minicart-quantity').empty().append(data.numItems);
    $('.minicart-link').attr({
        'aria-label': data.resources.minicartCountOfItems,
        title: data.resources.minicartCountOfItems
    });
    if (data.totals.orderLevelDiscountTotal.value > 0) {
        $('.order-discount').removeClass('hide-order-discount');
        $('.order-discount-total').empty()
            .append('- ' + data.totals.orderLevelDiscountTotal.formatted);
    } else {
        $('.order-discount').addClass('hide-order-discount');
    }

    if (data.totals.shippingLevelDiscountTotal.value > 0) {
        $('.shipping-discount').removeClass('hide-shipping-discount');
        $('.shipping-discount-total').empty().append('- ' +
            data.totals.shippingLevelDiscountTotal.formatted);
    } else {
        $('.shipping-discount').addClass('hide-shipping-discount');
    }

    data.items.forEach(function (item) {
        if (data.totals.orderLevelDiscountTotal.value > 0) {
            $('.coupons-and-promos').empty().append(data.totals.discountsHtml);
        }
        if (item.priceTotal.price.length > 0) {
            $('#cart-summary .product-line-' + item.UUID).empty().append(item.priceTotal.price);
        }

        if (item.renderedPromotions) {
            $('.item-' + item.UUID).empty().append(item.renderedPromotions);
        } else {
            $('.item-' + item.UUID).empty();
        }
        if (item.variationAttributes) {
            item.variationAttributes.forEach(function (lineItem) {
                $('span.'+ lineItem.attributeId +'-'+ item.UUID).empty().append(lineItem.displayValue);
            });
        } else {
            $('span.'+ item.attributeId +'-'+ item.UUID).parent().empty();
        }

        $('.uuid-' + item.UUID + ' .unit-price').empty().append(item.renderedPrice);
        $('.line-item-price-' + item.UUID + ' .unit-price').empty().append(item.renderedPrice);
        $('.item-total-' + item.UUID).empty().append(item.priceTotal.renderedPrice);

        // Update Summary's Line Item Product Name
        $('.uuid-' + item.UUID + ' .line-item-name span').empty().append(item.productName);
        // Update Summary's Line Item Product Price
        $('.uuid-' + item.UUID + ' .price-total').empty().append(item.priceTotal.price);
    });

    $('.donation-total').text(data.totals.totalDonation);

    //if the total is 0, remove the checkout button from the page
    if(data.totals.grandTotal === '$0.00'){
        $('.checkout-continue div a').hide(); //large screen button
        $('.checkout-continue-mobile div a').hide(); //small screen button
    }

    //set/update a cookie for the cart quantity
    //to ensure cookies get set based on the site, only set the domain for non-prod & Development sites
    if((document.location.host).includes('.northeastern.edu')){
        document.cookie = 'cartcount=' + data.numItems + '; path=/ ;samesite=lax; domain=northeastern.edu';
    }else{
        document.cookie = 'cartcount=' + data.numItems + '; path=/ ;samesite=lax';
    }
}

/**
 * When the cart is empty, this ensures the cartCount cookie is set to 0.
 */
 function initializeCookie() {
    var ccCookie = document.cookie ? document.cookie.split('; ').find((row) => row.startsWith('cartcount=')) : null;
    var cookieValue = ccCookie ? ccCookie.split('=')[1] : null;
    var miniCartLinkValue = $('.minicart-link').attr('title') ? $('.minicart-link').attr('title').split(' ')[0] : null;

    if(miniCartLinkValue && (cookieValue !== miniCartLinkValue)){
        //set/update a cookie for the cart quantity
        //to ensure cookies get set based on the site, only set the domain for non-prod & Development sites
        if((document.location.host).includes('.northeastern.edu')){
            document.cookie = 'cartcount=' + miniCartLinkValue + '; path=/ ;samesite=lax; domain=northeastern.edu';
        }else{
            document.cookie = 'cartcount=' + miniCartLinkValue + '; path=/ ;samesite=lax';
        }
    }

 }

/**
 * Displays an error alert in the cart when issues occur
 * @param {Object} message - Error message to display
 */
function createErrorNotification(message) {
    var errorHtml =
        `<div class="slds-notify slds-notify_alert slds-alert_error" role="alert" aria-labelledby="error-alert-title">
            <p>${message}</p>
            <div class="slds-notify__close">
                <button class="slds-button slds-button_icon slds-button_icon-small slds-button_icon-inverse" title="Close">
                <svg class="slds-button__icon" aria-hidden="true">
                    <use xlink:href="/assets/icons/utility-sprite/svg/symbols.svg#close"></use>
                </svg>
                <span class="slds-assistive-text">Close</span>
                </button>
            </div>
        </div>`

    $('.cart-error').append(errorHtml);
}

/**
 * re-renders the approaching discount messages
 * @param {Object} approachingDiscounts - updated approaching discounts for the cart
 */
function updateApproachingDiscounts(approachingDiscounts) {
    var html = '';
    $('.approaching-discounts').empty();
    if (approachingDiscounts.length > 0) {
        approachingDiscounts.forEach(function (item) {
            html += '<div class="single-approaching-discount text-center">'
                + item.discountMsg + '</div>';
        });
    }
    $('.approaching-discounts').append(html);
}

/**
 * Updates the availability of a product line item
 * @param {Object} data - AJAX response from the server
 * @param {string} uuid - The uuid of the product line item to update
 */
function updateAvailability(data, uuid) {
    var lineItem;
    var messages = '';

    for (var i = 0; i < data.items.length; i++) {
        if (data.items[i].UUID === uuid) {
            lineItem = data.items[i];
            break;
        }
    }

    if (lineItem != null) {
        $('.availability-' + lineItem.UUID).empty();

        if (lineItem.availability) {
            if (lineItem.availability.messages) {
                lineItem.availability.messages.forEach(function (message) {
                    messages += '<p class="line-item-attributes">' + message + '</p>';
                });
            }

            if (lineItem.availability.inStockDate) {
                messages += '<p class="line-item-attributes line-item-instock-date">'
                    + lineItem.availability.inStockDate
                    + '</p>';
            }
        }

        $('.availability-' + lineItem.UUID).html(messages);
    }
}

/**
 * Finds an element in the array that matches search parameter
 * @param {array} array - array of items to search
 * @param {function} match - function that takes an element and returns a boolean indicating if the match is made
 * @returns {Object|null} - returns an element of the array that matched the query.
 */
function findItem(array, match) { // eslint-disable-line no-unused-vars
    for (var i = 0, l = array.length; i < l; i++) {
        if (match.call(this, array[i])) {
            return array[i];
        }
    }
    return null;
}

/**
 * Updates details of a product line item
 * @param {Object} data - AJAX response from the server
 * @param {string} uuid - The uuid of the product line item to update
 */
function updateProductDetails(data, uuid) {
    $('.slds-card.product-info.uuid-' + uuid).replaceWith(data.renderedTemplate);
}

/**
 * Generates the modal window on the first call.
 *
 */
function getModalHtmlElement() {
    if ($('#editProductModal').length !== 0) {
        $('#editProductModal').remove();
    }
    var htmlString = '<!-- Modal -->'
        + '<div class="modal fade" id="editProductModal" tabindex="-1" role="dialog">'
        + '<span class="enter-message sr-only" ></span>'
        + '<div class="modal-dialog quick-view-dialog">'
        + '<!-- Modal content-->'
        + '<div class="modal-content">'
        + '<div class="modal-header">'
        + '    <button type="button" class="close pull-right" data-dismiss="modal">'
        + '        <span aria-hidden="true">&times;</span>'
        + '        <span class="sr-only"> </span>'
        + '    </button>'
        + '</div>'
        + '<div class="modal-body"></div>'
        + '<div class="modal-footer"></div>'
        + '</div>'
        + '</div>'
        + '</div>';
    $('body').append(htmlString);
}

/**
 * Parses the html for a modal window
 * @param {string} html - representing the body and footer of the modal window
 *
 * @return {Object} - Object with properties body and footer.
 */
function parseHtml(html) {
    var $html = $('<div>').append($.parseHTML(html));

    var body = $html.find('.product-quickview');
    var footer = $html.find('.modal-footer').children();

    return { body: body, footer: footer };
}

/**
 * replaces the content in the modal window for product variation to be edited.
 * @param {string} editProductUrl - url to be used to retrieve a new product model
 */
function fillModalElement(editProductUrl) {
    $('.modal-body').spinner().start();
    $.ajax({
        url: editProductUrl,
        method: 'GET',
        dataType: 'json',
        success: function (data) {
            var parsedHtml = parseHtml(data.renderedTemplate);

            $('#editProductModal .modal-body').empty();
            $('#editProductModal .modal-body').html(parsedHtml.body);
            $('#editProductModal .modal-footer').html(parsedHtml.footer);
            $('#editProductModal .modal-header .close .sr-only').text(data.closeButtonText);
            $('#editProductModal .enter-message').text(data.enterDialogMessage);
            $('#editProductModal').modal('show');
            $.spinner().stop();
        },
        error: function () {
            $.spinner().stop();
        }
    });
}

/**
 * replace content of modal
 * @param {string} actionUrl - url to be used to remove product
 * @param {string} productID - pid
 * @param {string} productName - product name
 * @param {string} uuid - uuid
 */
function confirmDelete(actionUrl, productID, productName, uuid) {
    var $deleteConfirmBtn = $('.cart-delete-confirmation');
    var $productToRemoveSpan = $('.product-to-remove');

    $deleteConfirmBtn.data('pid', productID);
    $deleteConfirmBtn.data('action', actionUrl);
    $deleteConfirmBtn.data('uuid', uuid);

    $productToRemoveSpan.empty().append(productName);
    $('#removeProductModal').modal('show'); //Display remove product modal
}

/**
 * Presents the confirmation model when clicking 'Move Gift to Favorites' button
 * @param {string} actionUrl - url to be used to remove product
 * @param {string} productID - pid
 * @param {string} productName - product name
 * @param {string} uuid - uuid
 */
function confirmMove(actionUrl, productID, productName, uuid) {
    var previousFocus = document.activeElement;
    var $moveConfirmBtn = $('.cart-move-confirmation');
    var $productToMoveSpan = $('.product-to-move');

    $moveConfirmBtn.data('pid', productID);
    $moveConfirmBtn.data('action', actionUrl);
    $moveConfirmBtn.data('uuid', uuid);
    $productToMoveSpan.empty().append(productName);

    var $modalElement = $('#moveProductModal')
    $modalElement.modal('show').find('.close').trigger( 'focus' );
    $modalElement.on('hidden.bs.modal', function () {
        if (previousFocus) {
            previousFocus.focus();
        }
    });
}

/**
 * Appends parameters to the URL and triggers an AJAX request to move the item to favorites.
 * @param {string} data - data returned from the server's ajax call
 */
 function removeFromCart(data) {
    $.spinner().stop();
    var messageType = data.success ? 'alert-success': 'alert-danger';
    var titleType = data.success ? 'success-alert-title' : 'error-alert-title';
    var status = data.success ? 'success' : 'error';
            
    $('#moveProductModal').modal('hide'); //Hide remove product modal           
    //only move if Wishlist-Add was sucessful, otherwise display previous lightbox of fund is already in the favorites.
    if (status == 'error' && $('.add-to-wishlist-messages').length === 0) {

        $('body').append(
            `<div class="add-to-wishlist-messages alert ${messageType} alert-dismissible" role="alert" aria-labelledby="${titleType}">
                <p class="alert__message">${data.msg}</p>
                <button type="button" class="close" data-dismiss="alert" aria-label="Close">
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>`
        ).find('.close').trigger( 'focus' );
    } else {
        // Checks if the product is in the favorites list; if not, it adds the product to the favorites and removes it from the cart.
        var productElement = $('.cart-remove-product[data-pid="' + data.pid + '"]')
        var productID = productElement.data('pid');
        var url = productElement.data('action');
        var uuid = productElement.data('uuid');
        var urlParams = {
            pid: productID,
            uuid: uuid
        };

        url = appendToUrl(url, urlParams);

        $('body > .modal-backdrop').remove();
        $('#moveProductModal').modal('hide'); //Hide move product modal

        removeProductFromCartAJAX(url, productID, uuid);
    }
}

/**
 * Initiating an AJAX request to transfer products to the Favorites list.
 * @param {string} actionUrl - url to be used to remove product
 * @param {string} productID - pid
 * @param {string} productName - product name
 * @param {string} uuid - uuid
 */
function cartMoveProduct(url, pid, optionId, optionVal, uuid) {
    if (!url || !pid) {
        return;
    }

    $.spinner().start();
    $.ajax({
        url: url,
        type: 'post',
        dataType: 'json',
        data: {
            pid: pid,
            optionId: optionId,
            optionVal: optionVal,
            uuid: uuid
        },
        success: function (data) {
            removeFromCart(data);
        },
        error: function (err) {
            if (err.responseJSON.redirectUrl) {
                window.location.href = err.responseJSON.redirectUrl;
            } else {
                createErrorNotification(err.responseJSON.errorMessage);
                $.spinner().stop();
            }
        }
    });

}

/**
 * Initiating an AJAX request to remove a product from the cart.
 * @param {string} actionUrl - url to be used to remove product
 * @param {string} productID - pid
 * @param {string} productName - product name
 * @param {string} uuid - uuid
 */
function removeProductFromCartAJAX(url, pid, uuid) {
    if (!url || !pid) {
        return;
    }
    
    $.spinner().start();
    $('body').trigger('cart:beforeUpdate');

    $.ajax({
        url: url,
        type: 'get',
        dataType: 'json',
        success: function (data) {
            setTimeout(function () {
                $.spinner().stop();
                $('.add-to-wishlist-messages').remove();
    
            }, 1500);

            if (data.basket.items.length === 0) {
                $('.minicart-quantity').empty().append(data.basket.numItems);
                $('.minicart-link').attr({
                    'aria-label': data.basket.resources.minicartCountOfItems,
                    title: data.basket.resources.minicartCountOfItems
                });
                $('.minicart .popover').empty();
                $('.minicart .popover').removeClass('show');

                $('.uuid-' + uuid).remove();

                updateCartTotals(data.basket);

                //display empty cart message
                $('.cart-empty').show();

            } else {
                if (data.toBeDeletedUUIDs && data.toBeDeletedUUIDs.length > 0) {
                    for (var i = 0; i < data.toBeDeletedUUIDs.length; i++) {
                        $('.uuid-' + data.toBeDeletedUUIDs[i]).remove();
                    }
                }
                $('.uuid-' + uuid).remove();
                if (!data.basket.hasBonusProduct) {
                    $('.bonus-product').remove();
                }
                $('.coupons-and-promos').empty().append(data.basket.totals.discountsHtml);
                updateCartTotals(data.basket);
                $('body').trigger('setShippingMethodSelection', data.basket);
                validateBasket(data.basket);
            }

            if(data.basket.subproContainsSubscriptions){
                //show recurring elements
                $('.recurringSignInMessage').addClass('slds-show').removeClass('slds-hide');

                //hide one time elements
                $('.guestCheckoutPanel').addClass('slds-hide').removeClass('slds-show');
                $('.onetimeSignInMessage').addClass('slds-hide').removeClass('slds-show');
                
            }else{
                //show one time elements
                $('.guestCheckoutPanel').addClass('slds-show').removeClass('slds-hide');
                $('.onetimeSignInMessage').addClass('slds-show').removeClass('slds-hide');
                
                //hide one time elements
                $('.recurringSignInMessage').addClass('slds-hide').removeClass('slds-show');
            }

            $('body').trigger('cart:update', data.basket);

            $.spinner().stop();
        },
        error: function (err) {
            $.spinner().stop();
            if (err.responseJSON.redirectUrl) {
                window.location.href = err.responseJSON.redirectUrl;
            } else {
                createErrorNotification(err.responseJSON.errorMessage);
                $.spinner().stop();
            }
        }
    });
}

module.exports = function () {

    //when the years to give drop-down is changed call to cart for updates
    $('body').on('change', '.cart-gift-payment-years', function(e) {
        //declare variables
        var selectedGiftFrequency = $(this).closest('.recurring-payment-date').siblings('.recurring-gift-frequency').find('.cart-gift-frequency option:selected')[0].value;
        var selectedYearsToDonate = e.currentTarget.value;
        var pId = e.currentTarget.getAttribute("data-pid");
        var url = e.currentTarget.getAttribute("data-action");
        var uuid = e.currentTarget.getAttribute("data-uuid");

        //AJAX call to update cart values
        if(url){

            var form = {
                pid: pId,
                uuid: uuid,
                donationYears: selectedYearsToDonate,
                giftFrequency: selectedGiftFrequency
            };

            //start the spinner over the card
            $(this).parents('.slds-card').spinner().start();

            //call server to update product line item
            $.ajax({
                url: url,
                type: 'post',
                data: form,
                success: function (data) {

                    $('body').trigger('cart:update', data.basket);
                    $.spinner().stop();
                },
                error: function (err) {
                    if (err.responseJSON.redirectUrl) {
                        window.location.href = err.responseJSON.redirectUrl;
                    } else {
                        createErrorNotification(err.responseJSON.errorMessage);
                        $.spinner().stop();
                    }
                }
            });
        }

    });

    //Prevent users from entering identified non-numerical characters
    $('body').on('keydown','.cart-gift-amount', function(e) {
        var invalidCharacters = ["-","+","e"];

        if((invalidCharacters.includes(e.key) || isNaN(e.key)) && e.keyCode != 8 && e.keyCode != 9 && e.key != '.'){
            e.preventDefault();
        }
    });

    //limit a user from inputting more than two decimal places in the gift amount
    $('body').on('input', '.cart-gift-amount', function(e) {
        //setup a regex expression
        const allowedPattern = new RegExp(/^(\d+(\.\d{0,2})?)?$/);
        const inputValue = $(this).val();

        if (allowedPattern.test(inputValue)) {
            $(this).data('prevValue', $(this).val());
        } else {
            $(this).val($(this).data('prevValue') || '');
        }
    }).trigger('input');

    //when focus moves away from the gift amount box, check for changes and valid amounts, call to cart for any required updates
    $('body').on('blur', '.cart-gift-amount', function(e) {

        //declare variables
        var inputAmount = parseFloat(e.currentTarget.value);
        var minAmount = parseInt(e.currentTarget.min);
        var maxAmount = parseInt(e.currentTarget.max);
        var cartAmount = parseFloat(e.currentTarget.getAttribute("data-cartamount"));
        var pId = e.currentTarget.getAttribute("data-pid");
        var isValid = false;
        var url = e.currentTarget.getAttribute("data-action");
        var uuid = e.currentTarget.getAttribute("data-uuid");
        var isChanged = cartAmount === inputAmount ? false : true;

        //show validation message for incorrect amount or clear existing message
        if(inputAmount < minAmount || isNaN(inputAmount)){
            $('#gift-alert-message-' + pId + '-under').removeAttr('hidden');
            $('#gift-alert-message-' + pId + '-over').prop('hidden','true');
        }else if(inputAmount > maxAmount){
            $('#gift-alert-message-' + pId + '-over').removeAttr('hidden');
            $('#gift-alert-message-' + pId + '-under').prop('hidden','true');
        }else{
            $('#gift-alert-message-' + pId + '-over').prop('hidden','true');
            $('#gift-alert-message-' + pId + '-under').prop('hidden','true');
            isValid = true;
        }

        //if the amount changes, AJAX call to update cart values
        if(isChanged && isValid && url){

            var form = {
                pid: pId,
                uuid: uuid,
                giftAmount: inputAmount
            };

            $(this).parents('.slds-card').spinner().start();

            //call server to update product line item
            $.ajax({
                url: url,
                type: 'post',
                data: form,
                success: function (data) {
                    //update the amounts on the input field to match the updated amount
                    e.currentTarget.setAttribute("data-cartamount",Number(inputAmount).toFixed(2));
                    e.currentTarget.value = Number(inputAmount).toFixed(2);
                    e.currentTarget.setAttribute("value",Number(inputAmount).toFixed(2));
                    updateCartTotals(data.basket);
                    $('body').trigger('cart:update', data.basket);
                    $.spinner().stop();
                },
                error: function (err) {
                    if (err.responseJSON.redirectUrl) {
                        window.location.href = err.responseJSON.redirectUrl;
                    } else {
                        createErrorNotification(err.responseJSON.errorMessage);
                        $.spinner().stop();
                    }
                }
            });
        }

    });

    //when the frequency drop-down is changed call to cart for updates
    $('body').on('change', '.cart-gift-frequency', function(e) {
        //declare variables
        var selectedFrequency = e.currentTarget.value;
        var pId = e.currentTarget.getAttribute("data-pid");
        var url = e.currentTarget.getAttribute("data-action");
        var uuid = e.currentTarget.getAttribute("data-uuid");

        //AJAX call to update cart values
        if(url){

            var form = {
                pid: pId,
                uuid: uuid,
                giftFrequency: selectedFrequency
            };

            //start the spinner over the card
            $(this).parents('.slds-card').spinner().start();

            //call server to update product line item
            $.ajax({
                url: url,
                type: 'post',
                data: form,
                success: function (data) {
                    //hide/show the monthly encouragement
                    if(data.isRecurring){
                        $('#'+e.currentTarget.id).parent().parent().siblings('.cart-monthly-upgrade').eq(0).attr('hidden', 'hidden');
                        $('#'+e.currentTarget.id).parent().parent().siblings('.recurring-payment-date').eq(0).removeAttr('hidden');
                    }else{
                        $('#'+e.currentTarget.id).parent().parent().siblings('.cart-monthly-upgrade').eq(0).removeAttr('hidden');
                        $('#'+e.currentTarget.id).parent().parent().siblings('.recurring-payment-date').eq(0).attr('hidden', 'hidden');
                    }

                    if(data.basket.subproContainsSubscriptions){
                        //show recurring elements
                        $('.recurringSignInMessage').addClass('slds-show').removeClass('slds-hide');
        
                        //hide one time elements
                        $('.guestCheckoutPanel').addClass('slds-hide').removeClass('slds-show');
                        $('.onetimeSignInMessage').addClass('slds-hide').removeClass('slds-show');
                        
                    }else{
                        //show one time elements
                        $('.guestCheckoutPanel').addClass('slds-show').removeClass('slds-hide');
                        $('.onetimeSignInMessage').addClass('slds-show').removeClass('slds-hide');
                        
                        //hide one time elements
                        $('.recurringSignInMessage').addClass('slds-hide').removeClass('slds-show');
                    }

                    $('body').trigger('cart:update', data.basket);
                    $.spinner().stop();
                },
                error: function (err) {
                    if (err.responseJSON.redirectUrl) {
                        window.location.href = err.responseJSON.redirectUrl;
                    } else {
                        createErrorNotification(err.responseJSON.errorMessage);
                        $.spinner().stop();
                    }
                }
            });
        }

    });

    //move button (aka Save to Favorites)
    $('body').on('click', '.cart-move-product', function (e) {
        e.preventDefault();

        var actionUrl = $(this).data('action');
        var productID = $(this).data('pid');
        var productName = $(this).data('name');
        var uuid = $(this).data('uuid');
        
        confirmMove(actionUrl, productID, productName, uuid);
    });

    // Displays confirmation dialog to remove fund from the cart
    $('body').on('click', '.cart-remove-product', function (e) {
        e.preventDefault();

        var actionUrl = $(this).data('action');
        var productID = $(this).data('pid');
        var productName = $(this).data('name');
        var uuid = $(this).data('uuid');

        if($('.minicart .popover').hasClass('show')){
            $('.minicart .popover').removeClass('show');
        }

        confirmDelete(actionUrl, productID, productName, uuid);
    });

    $('body').on('afterRemoveFromCart', function (e, data) {
        e.preventDefault();
        confirmDelete(data.actionUrl, data.productID, data.productName, data.uuid);
    });

    $('.optional-promo').click(function (e) {
        e.preventDefault();
        $('.promo-code-form').toggle();
    });

    // Confirmation (Yes) button on the "Move Gift to Favorites" confirmation modal
    $('body').on('click', '.cart-move-confirmation', function (e) {
        e.preventDefault();

        // Retrieve product details from the DOM to include in the cartMoveProduct AJAX request
        var productID = $(this).data('pid');
        var url = $(this).data('action');
        var uuid = $(this).data('uuid');
        var optionId = $(this).closest('.product-info').find('.lineItem-options-values').data('option-id');
        var optionVal = $(this).closest('.product-info').find('.lineItem-options-values').data('value-id');
        
        // Assign values if available; otherwise, set them to null.
        optionId = optionId || null;
        optionVal = optionVal || null;

        // Initiates an AJAX request to move the selected product to the favorites list
        cartMoveProduct(url, productID, optionId, optionVal, uuid);
    });

    $('body').on('click', '.cart-delete-cancel', function (e) {
        e.preventDefault();
        $('#removeProductModal').modal('hide'); //Hide remove product modal
    });



    // cartRemoveProductModal confirmation button
    $('body').on('click', '.cart-delete-confirmation', function (e) {
        e.preventDefault();
        var productID = $(this).data('pid');
        var url = $(this).data('action');
        var uuid = $(this).data('uuid');
        var urlParams = {
            pid: productID,
            uuid: uuid
        };

        url = appendToUrl(url, urlParams);

        $('body > .modal-backdrop').remove();
        $('#removeProductModal').modal('hide'); //Hide remove product modal

        removeProductFromCartAJAX(url, productID, uuid);
    });


    $('body').on('change', '.quantity-form > .quantity', function () {
        var preSelectQty = $(this).data('pre-select-qty');
        var quantity = $(this).val();
        var productID = $(this).data('pid');
        var url = $(this).data('action');
        var uuid = $(this).data('uuid');

        var urlParams = {
            pid: productID,
            quantity: quantity,
            uuid: uuid
        };
        url = appendToUrl(url, urlParams);

        $(this).parents('.slds-card').spinner().start();

        $('body').trigger('cart:beforeUpdate');

        $.ajax({
            url: url,
            type: 'get',
            context: this,
            dataType: 'json',
            success: function (data) {
                $('.cart-error').empty();
                $('.quantity[data-uuid="' + uuid + '"]').val(quantity);
                $('.coupons-and-promos').empty().append(data.totals.discountsHtml);
                updateCartTotals(data);
                updateAvailability(data, uuid);
                validateBasket(data);
                $(this).data('pre-select-qty', quantity);

                $('body').trigger('cart:update', data);

                $.spinner().stop();
                if ($(this).parents('.product-info').hasClass('bonus-product-line-item') && $('.cart-page').length) {
                    location.reload();
                }
            },
            error: function (err) {
                if (err.responseJSON.redirectUrl) {
                    window.location.href = err.responseJSON.redirectUrl;
                } else {
                    createErrorNotification(err.responseJSON.errorMessage);
                    $(this).val(parseInt(preSelectQty, 10));
                    $.spinner().stop();
                }
            }
        });
    });

    $('body').on('click', '.cart-page .bonus-product-button', function () {
        $.spinner().start();
        $(this).addClass('launched-modal');
        $.ajax({
            url: $(this).data('url'),
            method: 'GET',
            dataType: 'json',
            success: function (data) {
                base.methods.editBonusProducts(data);
                $.spinner().stop();
            },
            error: function () {
                $.spinner().stop();
            }
        });
    });

    $('body').on('hidden.bs.modal', '#chooseBonusProductModal', function () {
        $('#chooseBonusProductModal').remove();
        $('.modal-backdrop').remove();
        $('body').removeClass('modal-open');

        if ($('.cart-page').length) {
            $('.launched-modal .btn-outline-primary').trigger('focus');
            $('.launched-modal').removeClass('launched-modal');
        } else {
            $('.product-detail .add-to-cart').focus();
        }
    });

    $('body').on('click', '.cart-page .product-edit .edit, .cart-page .bundle-edit .edit', function (e) {
        e.preventDefault();

        var editProductUrl = $(this).attr('href');
        getModalHtmlElement();
        fillModalElement(editProductUrl);
    });

    $('body').on('shown.bs.modal', '#editProductModal', function () {
        $('#editProductModal').siblings().attr('aria-hidden', 'true');
        $('#editProductModal .close').focus();
    });

    $('body').on('hidden.bs.modal', '#editProductModal', function () {
        $('#editProductModal').siblings().attr('aria-hidden', 'false');
    });

    $('body').on('keydown', '#editProductModal', function (e) {
        var focusParams = {
            event: e,
            containerSelector: '#editProductModal',
            firstElementSelector: '.close',
            lastElementSelector: '.update-cart-product-global',
            nextToLastElementSelector: '.modal-footer .quantity-select'
        };
        focusHelper.setTabNextFocus(focusParams);
    });

    $('body').on('product:updateAddToCart', function (e, response) {
        // update global add to cart (single products, bundles)
        var dialog = $(response.$productContainer)
            .closest('.quick-view-dialog');

        $('.update-cart-product-global', dialog).attr('disabled',
            !$('.global-availability', dialog).data('ready-to-order')
            || !$('.global-availability', dialog).data('available')
        );
    });

    $('body').on('product:updateAvailability', function (e, response) {
        // bundle individual products
        $('.product-availability', response.$productContainer)
            .data('ready-to-order', response.product.readyToOrder)
            .data('available', response.product.available)
            .find('.availability-msg')
            .empty()
            .html(response.message);


        var dialog = $(response.$productContainer)
            .closest('.quick-view-dialog');

        if ($('.product-availability', dialog).length) {
            // bundle all products
            var allAvailable = $('.product-availability', dialog).toArray()
                .every(function (item) { return $(item).data('available'); });

            var allReady = $('.product-availability', dialog).toArray()
                .every(function (item) { return $(item).data('ready-to-order'); });

            $('.global-availability', dialog)
                .data('ready-to-order', allReady)
                .data('available', allAvailable);

            $('.global-availability .availability-msg', dialog).empty()
                .html(allReady ? response.message : response.resources.info_selectforstock);
        } else {
            // single product
            $('.global-availability', dialog)
                .data('ready-to-order', response.product.readyToOrder)
                .data('available', response.product.available)
                .find('.availability-msg')
                .empty()
                .html(response.message);
        }
    });

    $('body').on('product:afterAttributeSelect', function (e, response) {
        if ($('.modal.show .product-quickview .bundle-items').length) {
            $('.modal.show').find(response.container).data('pid', response.data.product.id);
            $('.modal.show').find(response.container).find('.product-id').text(response.data.product.id);
        } else {
            $('.modal.show .product-quickview').data('pid', response.data.product.id);
        }
    });

    $('body').on('change', '.quantity-select', function () {
        var selectedQuantity = $(this).val();
        $('.modal.show .update-cart-url').data('selected-quantity', selectedQuantity);
    });

    $('body').on('change', '.options-select', function () {
        var selectedOptionValueId = $(this).children('option:selected').data('value-id');
        $('.modal.show .update-cart-url').data('selected-option', selectedOptionValueId);
    });

    $('body').on('click', '.update-cart-product-global', function (e) {
        e.preventDefault();

        var updateProductUrl = $(this).closest('.cart-and-ipay').find('.update-cart-url').val();
        var selectedQuantity = $(this).closest('.cart-and-ipay').find('.update-cart-url').data('selected-quantity');
        var selectedOptionValueId = $(this).closest('.cart-and-ipay').find('.update-cart-url').data('selected-option');
        var uuid = $(this).closest('.cart-and-ipay').find('.update-cart-url').data('uuid');

        var form = {
            uuid: uuid,
            pid: base.getPidValue($(this)),
            quantity: selectedQuantity,
            selectedOptionValueId: selectedOptionValueId
        };

        $(this).parents('.card').spinner().start();

        $('body').trigger('cart:beforeUpdate');

        if (updateProductUrl) {
            $.ajax({
                url: updateProductUrl,
                type: 'post',
                context: this,
                data: form,
                dataType: 'json',
                success: function (data) {
                    $('#editProductModal').modal('hide');

                    $('.coupons-and-promos').empty().append(data.cartModel.totals.discountsHtml);
                    updateCartTotals(data.cartModel);
                    updateApproachingDiscounts(data.cartModel.approachingDiscounts);
                    updateAvailability(data.cartModel, uuid);
                    updateProductDetails(data, uuid);

                    if (data.uuidToBeDeleted) {
                        $('.uuid-' + data.uuidToBeDeleted).remove();
                    }

                    validateBasket(data.cartModel);

                    $('body').trigger('cart:update', data);

                    $.spinner().stop();
                },
                error: function (err) {
                    if (err.responseJSON.redirectUrl) {
                        window.location.href = err.responseJSON.redirectUrl;
                    } else {
                        createErrorNotification(err.responseJSON.errorMessage);
                        $.spinner().stop();
                    }
                }
            });
        }
    });

    $('.promo-code-form').submit(function (e) {
        e.preventDefault();
        $.spinner().start();
        $('.coupon-missing-error').hide();
        $('.coupon-error-message').empty();
        if (!$('#couponCode').val()) {
            $('.promo-code-form .form-control').addClass('is-invalid');
            $('.promo-code-form .form-control').attr('aria-describedby', 'missingCouponCode');
            $('.coupon-missing-error').show();
            $.spinner().stop();
            return false;
        }
        var $form = $('.promo-code-form');
        $('.promo-code-form .form-control').removeClass('is-invalid');
        $('.coupon-error-message').empty();
        $('body').trigger('promotion:beforeUpdate');

        $.ajax({
            url: $form.attr('action'),
            type: 'GET',
            dataType: 'json',
            data: $form.serialize(),
            success: function (data) {
                if (data.error) {
                    $('.promo-code-form .form-control').addClass('is-invalid');
                    $('.promo-code-form .form-control').attr('aria-describedby', 'invalidCouponCode');
                    $('.coupon-error-message').empty().append(data.errorMessage);
                    $('body').trigger('promotion:error', data);
                } else {
                    $('.coupons-and-promos').empty().append(data.totals.discountsHtml);
                    updateCartTotals(data);
                    updateApproachingDiscounts(data.approachingDiscounts);
                    validateBasket(data);
                    $('body').trigger('promotion:success', data);
                }
                $('#couponCode').val('');
                $.spinner().stop();
            },
            error: function (err) {
                $('body').trigger('promotion:error', err);
                if (err.responseJSON.redirectUrl) {
                    window.location.href = err.responseJSON.redirectUrl;
                } else {
                    createErrorNotification(err.errorMessage);
                    $.spinner().stop();
                }
            }
        });
        return false;
    });

    $('body').on('click', '.remove-coupon', function (e) {
        e.preventDefault();

        var couponCode = $(this).data('code');
        var uuid = $(this).data('uuid');
        var $deleteConfirmBtn = $('.delete-coupon-confirmation-btn');
        var $productToRemoveSpan = $('.coupon-to-remove');

        $deleteConfirmBtn.data('uuid', uuid);
        $deleteConfirmBtn.data('code', couponCode);

        $productToRemoveSpan.empty().append(couponCode);
    });

    $('body').on('click', '.delete-coupon-confirmation-btn', function (e) {
        e.preventDefault();

        var url = $(this).data('action');
        var uuid = $(this).data('uuid');
        var couponCode = $(this).data('code');
        var urlParams = {
            code: couponCode,
            uuid: uuid
        };

        url = appendToUrl(url, urlParams);

        $('body > .modal-backdrop').remove();

        $.spinner().start();
        $('body').trigger('promotion:beforeUpdate');
        $.ajax({
            url: url,
            type: 'get',
            dataType: 'json',
            success: function (data) {
                $('.coupon-uuid-' + uuid).remove();
                updateCartTotals(data);
                updateApproachingDiscounts(data.approachingDiscounts);
                validateBasket(data);
                $.spinner().stop();
                $('body').trigger('promotion:success', data);
            },
            error: function (err) {
                $('body').trigger('promotion:error', err);
                if (err.responseJSON.redirectUrl) {
                    window.location.href = err.responseJSON.redirectUrl;
                } else {
                    createErrorNotification(err.responseJSON.errorMessage);
                    $.spinner().stop();
                }
            }
        });
    });

    initializeCookie();
    base.selectAttribute();
    base.colorAttribute();
    base.removeBonusProduct();
    base.selectBonusProduct();
    base.enableBonusProductSelection();
    base.showMoreBonusProducts();
    base.addBonusProductsToCart();
    base.focusChooseBonusProductModal();
    base.trapChooseBonusProductModalFocus();
    base.onClosingChooseBonusProductModal();
};
